import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
  category: string
}

export default function DecksGallery() {
  // State for filtered images and active filter
  const [activeFilter, setActiveFilter] = useState('all')
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'
  
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Function to parse image filename into alt text
  function parseImageFileName(filename: string): string {
    // Remove file extension
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    // Replace hyphens with spaces
    return withoutExtension.replace(/-/g, ' ')
  }
  
  // Function to fix path for production and development consistency
  function fixImagePath(path: string): string {
    // Remove /public prefix if it exists (for dev mode)
    let imagePath = path.replace('/public', '')
    
    // Ensure path starts with a slash
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    
    return imagePath
  }

  // Load images dynamically
  useEffect(() => {
    try {
      // Using Vite's import.meta.glob to get all images
      const imageModules = import.meta.glob('/public/assets/images/Decks/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      // Convert the modules to ImageItem objects
      Object.keys(imageModules).forEach(path => {
        const imagePath = fixImagePath(path)
        const category = path.includes('/Composite Decks/') ? 'composite' :
                        path.includes('/Wood Decks/') ? 'wood' :
                        path.includes('/Vinyl Decks/') ? 'vinyl' : 'other'
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText,
          category
        })
      })

      // Filter images based on active filter
      if (activeFilter === 'all') {
        setDisplayedImages(loadedImages)
      } else {
        setDisplayedImages(loadedImages.filter(img => img.category === activeFilter))
      }
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [activeFilter])

  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  // Filter buttons
  const filterOptions = [
    { id: 'all', label: 'All' },
    { id: 'composite', label: 'Composite Decks' },
    { id: 'wood', label: 'Wood Decks' },
    { id: 'vinyl', label: 'Vinyl Decks' }
  ]

  return (
    <main>
      <Helmet>
        <title>Deck Photo Gallery | Busy Beaver Construction | Calgary</title>
        <meta name="description" content="Browse our gallery of custom deck projects in Calgary featuring composite, wood, and vinyl decks. Get inspiration for your outdoor living space from our portfolio." />
        <meta name="keywords" content="deck gallery, deck photos, custom decks Calgary, composite decks, wood decks, vinyl decks, deck design ideas, outdoor living spaces" />
        <link rel="canonical" href="https://busybeaverconstruction.com/gallery/decks" />
        <meta property="og:title" content="Deck Photo Gallery | Busy Beaver Construction" />
        <meta property="og:description" content="View our portfolio of custom-built decks in Calgary including TimberTech composite, cedar, pressure-treated, and vinyl deck projects for inspiration." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/gallery/decks" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg" as="image" />
      </Helmet>
      <Header />
      
      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg"
          alt="TimberTech Terrain deck with Brown Oak trim and Sandy Birch surface by Busy Beaver Construction Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4  px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Affordable Top Quality Decks and Fences</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Decking Services</h1>
           
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 text-base text-gray-500 leading-relaxed">
              <p>
                At Busy Beaver Construction, all of our decks are designed with the customer's preferences and needs in mind. 
                Decks are a great feature to add to a new or existing home, we replace old decks or build new ones.
              </p>
              
              <p>
                The ideal deck should fit the aesthetic style of the house, and coincide with your backyard layout. 
                Decks can be a valuable addition to your home and transform your backyard space into a functional outdoor living space.
              </p>
              
              <p>
                Property owners have access to a wide variety of decking materials, allowing homeowners to easily find a material 
                that suits their budget. We install decks using various types of products you can find in the market whether it be, 
                Pressure Treated wood, Cedar, Composite/PVC, and Vinyl.
              </p>
              
              <p>
                A wood deck will require the most maintenance, but gives you a more natural look of a traditional style deck. 
                We use top quality Pressure Treated and Cedar wood products from the finest lumber distributers in Calgary.
              </p>
              
              <p>
                Maintenance free products, such as composite and PVC decking, have become increasingly popular due to its durability 
                and resiliency to our climate. Various color options have become more readily available since composites initial entry 
                into the market. Our team has the expertise to install any brand of composite on the market. As a testament to its durability, 
                most composite decking is backed by a 25-30 year warranty.
              </p>
              
              <p>
                Busy Beaver Construction is a <strong>Platinum Timbertech Installer</strong>. Platinum installers are a specialized group, 
                who are the leading contractors in the market, and have demonstrated expertise in the installation of Timbertech Azek products.
              </p>
              
              <p className="text-beaver-red">
                Our gallery of past projects completed by Busy Beaver Construction will offer useful ideas and options to consider for your deck.
              </p>
              
            </div>
            
            <div className="space-y-6 ">
              <div className="overflow-hidden h-[320px] md:h-[420px]">
                <img 
                  src="/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg"
                  alt="Large outdoor seating area with custom cedar privacy wall by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[420px]">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg"
                  alt="TimberTech Terrain deck with Brown Oak trim and Sandy Birch surface installation in Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Deck Gallery</h1>
          </div>
          
          {/* Filter Buttons */}
          <div className="flex justify-center flex-wrap gap-2 mb-8">
            {filterOptions.map(option => (
              <button
                key={option.id}
                onClick={() => setActiveFilter(option.id)}
                className={`px-4 py-2 rounded transition-colors ${
                  activeFilter === option.id
                    ? 'bg-busy-green text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[280px] md:h-[320px]">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-500">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* No results message */}
          {displayedImages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700">Loading images...</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <p className="mb-6 text-gray-700">
              Contact us today to discuss your custom deck project and bring your vision to life.
            </p>
            <a
              href="/estimate" 
              className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:bg-beaver-green-dark hover:brightness-110 transition-all duration-300"
              onClick={(e) => handleNavigation(e, '/estimate')}
            >
              GET FREE ESTIMATE
            </a>
          </div>
        </div>
      </section>
      
      {/* WE DO DAM GOOD WORK Section */}
      <section className="py-10 bg-gray-900 text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-wide">
          WE DO <span className="text-red-600">DAM</span> GOOD WORK
        </h2>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Image caption */}
              <div 
                className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white py-2 px-4 rounded text-center max-w-lg transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                {displayedImages[lightboxSlide].alt}
              </div>
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
} 